import { Button, Label } from '@monksflow/monks-ui';
import { format, startOfToday } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { resolveDateFnsLocale } from '@/shared/i18n/date-fns-locale';
import { DatePicker } from '@/shared/ui/DatePicker/DatePicker';

interface Props {
  value: string | null;
  onChange: (next: string | null) => void;
  disabled?: boolean;
}

function stringToDate(value: string | null): Date | null {
  if (!value) return null;
  const [year, month, day] = value.split('-').map(Number);
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return null;
  return new Date(year, month - 1, day);
}

function dateToString(date: Date | null): string | null {
  if (!date) return null;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function todayUtcYmd(): string {
  const now = new Date();
  const year = now.getUTCFullYear();
  const month = String(now.getUTCMonth() + 1).padStart(2, '0');
  const day = String(now.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function GenerationEndDateField({ value, onChange, disabled = false }: Props) {
  const { t, i18n } = useTranslation('admin');

  const selected = stringToDate(value);
  const hasPassed = value !== null && todayUtcYmd() > value;
  const locale = resolveDateFnsLocale(i18n.language);

  return (
    <div className="flex flex-col gap-2xs">
      <Label htmlFor="workspace-generation-end-date" className="font-normal">
        {t('admin.workspaceScheduleSection.generationEndDateLabel')}
      </Label>
      <p className="text-body-sm text-muted-foreground">
        {t('admin.workspaceScheduleSection.generationEndDateDescription')}
      </p>
      <div className="flex items-center gap-xs">
        <DatePicker
          value={selected}
          onChange={(next) => onChange(dateToString(next))}
          disabled={disabled}
          id="workspace-generation-end-date"
        >
          <DatePicker.Trigger
            placeholder={t('admin.workspaceScheduleSection.generationEndDatePlaceholder')}
            className="flex-1 min-w-0"
          />
          <DatePicker.Content disabledDates={{ before: startOfToday() }} />
        </DatePicker>
        {value !== null && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onChange(null)}
            disabled={disabled}
          >
            {t('admin.workspaceScheduleSection.clearGenerationEndDate')}
          </Button>
        )}
      </div>
      <p className="text-body-sm text-muted-foreground">
        {t('admin.workspaceScheduleSection.generationEndDateInterpretedAsUtc')}
      </p>
      {hasPassed && selected && (
        <p className="text-body-sm text-muted-foreground">
          {t('admin.workspaceScheduleSection.generationEndedOn', {
            date: format(selected, 'PPP', { locale }),
          })}
        </p>
      )}
    </div>
  );
}
